
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link, useLocation } from "react-router-dom";
import { useState, memo } from "react";
import {
  LayoutDashboard,
  Code,
  BookOpen,
  Database,
  Users,
  Settings,
  Shield,
  Key,
  FileText,
  Search,
  Download,
  BarChart3,
  HelpCircle,
  Activity,
  CreditCard,
  Menu,
  Sparkles,
  Zap,
  Folder,
  MessageSquare,
} from "lucide-react";
import { useRoleAccess } from "@/hooks/useRoleAccess";

const Sidebar = memo(function Sidebar({ className }: React.HTMLAttributes<HTMLDivElement>) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { canAccessSettings, canAccessAuditLogs, canExportReports, canInviteMembers, isViewer, isSuperAdmin } = useRoleAccess();
  
  const showGettingStarted = true; // Always show Getting Started

  const navItems = [
    // Getting Started (conditional)
    ...(showGettingStarted ? [{ href: "/getting-started", icon: Sparkles, label: "Getting Started" }] : []),
    
    // Main sections
    { href: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    
    // API & Development (prominently featured)
    { 
      label: "API & Development", 
      isSection: true,
      items: [
        { href: "/api-keys", icon: Key, label: "API Keys" },
        { href: "/chatbot", icon: Code, label: "Chatbot" },
        { href: "/memory-sources", icon: Database, label: "Memory Sources" },
        { href: "/usage-stats", icon: Activity, label: "Usage Stats" },
        { href: "https://docs.recallio.ai/api-reference", icon: BookOpen, label: "API Documentation", external: true },
        //{ href: "/webhooks", icon: Code, label: "Webhooks" },
      ]
    },
    
    // Compliance & Governance
    { 
      label: "Compliance", 
      isSection: true,
      items: [
        { href: "/memory-entries", icon: Database, label: "Memory Entries" },
        { href: "/chatbot-history", icon: Search, label: "Chatbot History" },
        ...(canAccessAuditLogs ? [{ href: "/audit-logs", icon: BarChart3, label: "Audit Logs" }] : []),
        { href: "/playground", icon: Zap, label: "Playground" },
        ...(canExportReports ? [{ href: "/export-delete", icon: Download, label: "Export & Delete" }] : [])
      ]
    },
    
    // Account & Settings
    { 
      label: "Account", 
      isSection: true,
      items: [
        { href: "/team-management", icon: Users, label: "Team Management" },
        { href: "/billing", icon: CreditCard, label: "Billing & Accounts" },
        { href: "/projects", icon: Folder, label: "Projects" },
        ...(canAccessSettings ? [{ href: "/settings", icon: Settings, label: "Settings" }] : []),
      ]
    },

    ...(isSuperAdmin ? [{
      label: "Super Admin",
      isSection: true,
      items: [
        { href: "/demo", icon: Shield, label: "Demo" },
        { href: "/superadmin", icon: MessageSquare, label: "All Chats" },
      ]
    }] : []),
    
    // Support
    { 
      label: "Support", 
      isSection: true,
      items: [
        { href: "https://docs.recallio.ai", icon: HelpCircle, label: "Help & Support", external: true },
        { href: "/status", icon: Activity, label: "System Status" },
      ]
    },
  ];

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-6 py-4">
        <img 
          src="/lovable-uploads/4eecbb54-a517-42eb-bf2a-f06ad5d82aee.png" 
          alt="Recallio Logo" 
          className="h-8 w-auto"
        />
        {isViewer && (
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
            Viewer
          </span>
        )}
      </div>
      
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-2">
          {navItems.map((item, index) => {
            if (item.isSection) {
              return (
                <div key={index} className="py-2">
                  <h3 className="mb-2 px-3 text-xs font-semibold tracking-tight text-muted-foreground uppercase">
                    {item.label}
                  </h3>
                  <div className="space-y-1">
                    {item.items?.map((subItem) => {
                      const isExternal = Boolean((subItem as { external?: boolean }).external);
                      const isActive = !isExternal && location.pathname === subItem.href;

                      return (
                        <Button
                          key={subItem.href}
                          variant={isActive ? "secondary" : "ghost"}
                          className="w-full justify-start h-9 text-sm"
                          asChild
                          onClick={() => {
                            if (!isExternal) {
                              setIsOpen(false);
                            }
                          }}
                        >
                          {isExternal ? (
                            <a href={subItem.href} target="_blank" rel="noopener noreferrer">
                              <subItem.icon className="mr-2 h-4 w-4" />
                              <span>{subItem.label}</span>
                            </a>
                          ) : (
                            <Link to={subItem.href}>
                              <subItem.icon className="mr-2 h-4 w-4" />
                              <span>{subItem.label}</span>
                            </Link>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            return (
              <Button
                key={item.href}
                variant={location.pathname === item.href ? "secondary" : "ghost"}
                className="w-full justify-start h-9 text-sm"
                asChild
                onClick={() => setIsOpen(false)}
              >
                <Link to={item.href!}>
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              </Button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <>
      {/* Mobile Sidebar */}
      <div className="md:hidden">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="fixed top-4 left-4 z-50 bg-background border border-border shadow-lg">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <div className={cn("hidden md:block fixed left-0 top-0 z-40 h-screen w-64 border-r bg-background", className)}>
        <SidebarContent />
      </div>
    </>
  );
});

export default Sidebar;
